import { memo, useCallback, useMemo, useState } from "react";
import { generatePath, useNavigate } from "react-router";

import { HotelTemplateType, type HotelTopQuery } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import type { ButtonProps } from "@/components/ui/button/button";
import { Button } from "@/components/ui/button/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog/dialog";
import { InputTextFieldNew } from "@/components/ui/form/fields-new/input-field/input-text";

import { useResetHotelTemplateSettings } from "@/components/features-new/hotel-top/api";

import { StepHeader } from "../_step-header";

import type { ChangeEvent } from "react";

type Props = {
	hotelTemplateSettings: HotelTopQuery["hotelTemplateSettings"];
};

const CONFIRM_TEXT = "リセットする";

export const ResetHotelTemplateType = memo(({ hotelTemplateSettings }: Props) => {
	const { resetHotelTemplateSettings } = useResetHotelTemplateSettings();
	const [confirmText, setConfirmText] = useState("");

	const buttonDisabled = useMemo(() => {
		return confirmText !== CONFIRM_TEXT;
	}, [confirmText]);

	const buttonVariant = useMemo<ButtonProps["variant"]>(() => {
		return buttonDisabled ? "disabled" : "destructive";
	}, [buttonDisabled]);

	const handleConfirmTextChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setConfirmText(e.target.value);
	}, []);

	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const navigate = useNavigate();
	const navigateToHotelTop = useCallback(() => {
		navigate(generatePath(PATHS.HOTEL.SELECT_HOTEL_TEMPLATE, { hotelID: hotelTemplateSettings.hotelID }));
	}, [hotelTemplateSettings.hotelID, navigate]);

	const handleResetButtonClick = useCallback(async () => {
		showLoadingScreenCallback();
		const { data, errors } = await resetHotelTemplateSettings({
			hotelID: hotelTemplateSettings.hotelID,
		}).finally(hideLoadingScreenCallback);

		if (!data || errors) {
			return;
		}

		notifyUserEventInfo({
			title: "テンプレート選択によって作成されたデータをリセットしました",
			description: "テンプレート選択画面に遷移します",
		});
		navigateToHotelTop();
	}, [
		hideLoadingScreenCallback,
		hotelTemplateSettings.hotelID,
		navigateToHotelTop,
		resetHotelTemplateSettings,
		showLoadingScreenCallback,
	]);

	if (
		!hotelTemplateSettings.hotelTemplateTypeSelectable ||
		hotelTemplateSettings.hotelTemplateType === HotelTemplateType.NotSelected
	) {
		// テンプレート選択ができない or テンプレート未選択の場合は表示しない
		return null;
	}

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<StepHeader title="テンプレート選択によって自動で作成されたデータをリセットする" description="" />
				<p>
					データをリセットすることで、
					自動で作成された全てのプラン・部屋・オプションのデータが全て削除されます。
				</p>
				<p>自分で作成したプラン・部屋・オプションのデータは削除されません。</p>
			</div>

			<Dialog>
				<DialogTrigger asChild>
					<Button variant="destructive">リセットする</Button>
				</DialogTrigger>

				<DialogContent>
					<DialogHeader>
						<DialogTitle>データのリセット</DialogTitle>
						<DialogDescription></DialogDescription>
					</DialogHeader>

					<div className="space-y-10">
						<div className="space-y-1">
							<p>自動で作成された全てのプラン・部屋・オプションのデータが削除されます。</p>
							<p>自分で作成したプラン・部屋・オプションのデータは削除されません。</p>
						</div>

						<div className="space-y-4">
							<InputTextFieldNew
								label={`リセットする場合は "${CONFIRM_TEXT}"と入力してください`}
								placeholder={CONFIRM_TEXT}
								value={confirmText}
								onChange={handleConfirmTextChange}
							/>

							<div className="flex justify-end">
								<Button
									variant={buttonVariant}
									onClick={handleResetButtonClick}
									disabled={buttonDisabled}
								>
									リセットする
								</Button>
							</div>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</div>
	);
});

ResetHotelTemplateType.displayName = "ResetHotelTemplateType";
